import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useRef, useEffect, useState } from "react";

const stats = [
  { value: 1000, prefix: "$", suffix: "", label: "Assets under management" },
  { value: 54, prefix: "", suffix: "+", label: "Portfolio companies backed" },
  { value: 55, prefix: "", suffix: "", label: "Countries where portfolio companies operate" },
  { value: 89, prefix: "", suffix: "%", label: "Follow-on participation rate" },
  { value: 77, prefix: "", suffix: "+", label: "Senior operators and advisors in our network" },
];

function AnimatedNumber({ value, prefix, suffix, isInView }) {
  const [displayValue, setDisplayValue] = useState(0);
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v) => Math.round(v));

  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1],
      });
      const unsubscribe = rounded.on("change", (v) => setDisplayValue(v));
      return () => {
        controls.stop();
        unsubscribe();
      };
    }
  }, [isInView, value, motionValue, rounded]);

  return (
    <span className="font-heading text-5xl sm:text-6xl font-medium text-blue-600 tracking-tighter tabular-nums">
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
}

export default function StatsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      data-testid="stats-section"
      className="py-[30px] md:py-[50px]"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-[45px] md:mb-[65px]"
        >
          <h2
            data-testid="stats-heading"
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a]"
          >
            Nebula Flow at a glance
          </h2>
        </motion.div>

        {/* Stats grid */}
        <div className="flex flex-col gap-8 md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-6 lg:gap-0">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              data-testid={`stat-item-${i + 1}`}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] }}
              className={`text-center px-4 ${
                i < stats.length - 1 ? "md:stat-separator" : ""
              } ${i === stats.length - 1 && stats.length % 2 !== 0 ? "md:col-span-2 lg:col-span-1" : ""}`}
            >
              <AnimatedNumber
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
                isInView={isInView}
              />
              <p className={`font-body text-[16px] text-[#475569] mt-3 mx-auto leading-snug ${
                stat.label === "Countries where portfolio companies operate"
                  ? "max-w-[220px] md:max-w-[200px]"
                  : stat.label === "Senior operators and advisors in our network"
                  ? "max-w-[250px] md:max-w-[200px]"
                  : "max-w-none md:max-w-[160px]"
              }`}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
